import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { Station } from '@mi-front/core';
import { Avatar, AvatarProps } from '@mui/material';

interface Props extends Omit<AvatarProps, 'src' | 'alt' | 'children'> {
  readonly stationId: string;
  readonly size?: number | string;
  /**
   * When set, logo and name are taken from this object instead of the stations cache.
   * Use when the caller already holds the station DTO (e.g. filter chips).
   */
  readonly station?: Pick<Station, 'name' | 'logoUrl'> | null;
}

/**
 * Renders an avatar for a station by id (logo or first letter of name).
 */
export const StationAvatar = (props: Props) => {
  const { stationId, station: stationFromProps, size = 24, sx, ...rest } = props;

  const finder = useStationsFinder();
  const station = stationFromProps ?? finder(stationId);
  const logoUrl = station?.logoUrl || '';
  const name = station?.name ?? '';
  const fallbackLetter = name ? name.charAt(0).toUpperCase() : '?';

  const sizeValue = typeof size === 'number' ? `${size}px` : size;
  const fontSize = typeof size === 'number' ? `${size * 0.3125}px` : '0.75rem';

  return (
    <Avatar
      src={logoUrl}
      alt={name ? `${name} logo` : 'Station logo'}
      sx={{
        width: sizeValue,
        height: sizeValue,
        fontSize,
        border: '1px solid',
        borderColor: 'grey.400',
        ...sx,
      }}
      {...rest}
    >
      {fallbackLetter}
    </Avatar>
  );
};
